import { type ErrorCode, isErrorCode } from "./constants/error-codes.constants";

/**
 * The shape a failure takes on the wire: a human-readable half (safe to show,
 * translated, never compared against) and a machine half (stable, compared
 * against, never shown).
 *
 * Which field carries the machine half differs by producer, for historical
 * reasons this type does not try to relitigate:
 *
 * - core-api's legacy handler sends `{ error: <human>, code: <machine> }`
 *   (`errorHandler`).
 * - core-api v4 sends `{ error: <human>, reason: <machine> }` for an exception
 *   thrown with a structured `reason` (`AllExceptionsFilter`).
 * - apps/web BFF handlers send `{ error: <machine>, message: <human> }` — the
 *   convention already shared by the signup and public-apply endpoints.
 *
 * `readErrorCode` accepts all three, so callers never need to know which
 * producer they are talking to.
 */
export type ErrorEnvelope = {
  error: string;
  message?: string;
  code?: ErrorCode;
  reason?: string;
};

/**
 * Pulls the machine code out of an untrusted body. Anything unrecognised — a
 * prose message in `error`, a code this build has not heard of, a non-object —
 * reads as absent, so callers fall through to their generic path instead of
 * throwing.
 */
export function readErrorCode(body: unknown): ErrorCode | undefined {
  if (typeof body !== "object" || body === null) return undefined;
  const code = "code" in body ? body.code : undefined;
  if (isErrorCode(code)) return code;
  const reason = "reason" in body ? body.reason : undefined;
  if (isErrorCode(reason)) return reason;
  const error = "error" in body ? body.error : undefined;
  return isErrorCode(error) ? error : undefined;
}

/** As above, for the human-readable half. */
export function readErrorMessage(body: unknown): string | undefined {
  if (typeof body !== "object" || body === null) return undefined;
  const message = "message" in body ? body.message : undefined;
  return typeof message === "string" ? message : undefined;
}
